// 판매장부 위 차트 둘(손익 워터폴 · 상품 진단 TOP 10)의 숫자 셈.
// 화면(growthSalesLedger.vue)은 그리기만 한다 — 값은 표가 쓰는 행(dailyRows·productRows)에서만 나온다.
// 표와 차트가 어긋나면 사장님이 둘 중 무엇을 믿을지 모르게 된다(판 #725 · PR #406 살려 새로 짓기).

const sumOf = (rows, key) => rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

// 금액을 짧게 (축 라벨·막대 라벨용). 표와 툴팁은 원 단위 그대로 쓴다
export function formatMoneyShort(v) {
    const abs = Math.abs(v);
    if (abs >= 100000000) return (v / 100000000).toFixed(1) + '억';
    if (abs >= 10000) return Math.round(v / 10000).toLocaleString() + '만';
    return Math.round(v).toLocaleString();
}

// 손익 워터폴 (조회기간 합계)
// 소계(순매출·기여이익·총수익)는 표 열의 합계를 그대로 쓴다. 차감 막대는 그 사이를 잇고,
// 표가 따로 반올림한 몫·이름 없는 잔 비용은 «기타 판매비용» 이 받는다 — 그래서 막대가 늘 맞물린다.
// 반환: { bars: [{ label, kind: start|delta|subtotal|end, delta, from, to }], etcDetail, 순매출액 }
export function buildWaterfall(rows) {
    rows = rows || [];
    if (rows.length === 0) return { bars: [], etcDetail: {}, 순매출액: 0 };

    const sum = (key) => sumOf(rows, key);
    const 총매출액 = sum('일별총매출액');
    const 순매출액 = sum('일별순매출액');
    const 기여이익 = sum('일별기여이익');
    const 총수익 = sum('일별총수익');

    // 순매출의 0.3% 이하라 막대가 보이지 않는 항목들 — 하나로 묶고 툴팁에서 내역을 펼친다
    const etcDetail = {
        반출비: sum('일별반출비'),
        반출배송비: sum('일별반출배송비'),
        바코드비: sum('일별바코드비'),
        밀크런비용: sum('일별밀크런비용'),
        재고손실보상: -sum('일별재고손실보상') // 보상은 비용을 깎으므로 부호가 반대
    };

    const named = [
        ['수수료', sum('일별수수료')],
        ['배송비', sum('일별배송비')],
        ['입출고비', sum('일별입출고비')],
        ['보관비', sum('일별보관비')],
        ['반품회수·재입고비', sum('일별반품회수재입고비')],
        ['광고비', sum('일별광고비')]
    ];
    // 사장님이 직접 넣은 기타비용(외부 마케팅 등) — 없으면 막대도 없다
    const 외부 = sum('일별외부마케팅비');
    if (외부 !== 0) named.push(['기타비용(직접 입력)', 외부]);
    const namedTotal = named.reduce((acc, [, v]) => acc + v, 0);
    const 기타판매비용 = (순매출액 - 기여이익) - namedTotal;

    // 매입가 없는 상품은 기여이익엔 들고 총수익엔 안 든다(원가를 모르면 이익도 모른다).
    // 조용히 빼면 끝 막대가 표와 어긋나므로 따로 밝힌다
    const 매입원가 = sum('일별매입원가');
    const 부가세 = sum('일별부가세');
    const 미등록 = 기여이익 - 매입원가 - 부가세 - 총수익;

    const steps = [
        ['총매출액', 총매출액, 'start'],
        ['할인쿠폰', 순매출액 - 총매출액, 'delta'],
        ['순매출액', 순매출액, 'subtotal'],
        ...named.map(([label, v]) => [label, -v, 'delta']),
        ['기타 판매비용', -기타판매비용, 'delta'],
        ['기여이익', 기여이익, 'subtotal'],
        ['매입원가', -매입원가, 'delta'],
        ['부가세', -부가세, 'delta']
    ];
    if (미등록 !== 0) steps.push(['매입가 미등록 상품', -미등록, 'delta']);
    steps.push(['총수익', 총수익, 'end']);

    let running = 0;
    const bars = steps.map(([label, value, kind]) => {
        if (kind === 'start' || kind === 'subtotal' || kind === 'end') {
            running = value;
            return { label, kind, delta: null, from: 0, to: value };
        }
        const from = running;
        running = from + value;
        return { label, kind, delta: value, from, to: running };
    });

    return { bars, etcDetail, 순매출액 };
}

// ===== 상품 진단 TOP 10 =====
// 산점도(매출 x ROI)는 SKU 수백 개인 계정에서 점이 뭉개져 걷어냈다(#406). 상위 10개만 막대로 비교한다.
// productRows(기타비용 얹힌 상품별 표 행)와 재고 스냅샷(coverageOf(vid))을 받는다.
// ROI 는 표의 총수익·매입원가로 다시 센다 — 옛 roi 칸은 기타비용이 얹히기 전 값이라 표와 어긋난다.
export function buildDiagnostics(productRows, coverageOf, sort) {
    const cover = typeof coverageOf === 'function' ? coverageOf : () => ({});
    const items = (productRows || [])
        .filter(p => p.cogs > 0 && p.grossProfit !== null && p.grossProfit !== undefined && p.saleAmount > 0)
        .map(p => {
            const c = cover(p.vendorItemId) || {};
            // 이름 모르는 상품은 표 행 상품명이 «, »(빈 이름 둘을 이은 것)로 온다 — 쉼표만 띄우지 않고 옵션 번호로
            const 상품명 = (p.상품명 || '').replace(/^[\s,]+$/, '');
            return {
                vendorItemId: p.vendorItemId,
                name: p.판매상품명 || 상품명 || '옵션 ' + p.vendorItemId,
                saleAmount: Math.round(p.saleAmount || 0),
                grossProfit: Math.round(p.grossProfit || 0),
                // 일자 행·상품 행과 같은 공급가(부가세 제외) 기준
                roi: Math.round((p.grossProfit / (p.cogs * 10 / 11)) * 100 * 10) / 10,
                stockAmount: Math.round(c.totalStockAmount || 0),
                // getCoverage 꼴 = totalStockDays(재고 스냅샷 stockDays 를 옮긴 이름)
                stockDays: Math.round(c.totalStockDays || 0)
            };
        });

    const cmp = {
        stock: (a, b) => b.stockAmount - a.stockAmount,
        sales: (a, b) => b.saleAmount - a.saleAmount,
        loss: (a, b) => a.grossProfit - b.grossProfit,
        roi: (a, b) => a.roi - b.roi
    }[sort] || ((a, b) => b.stockAmount - a.stockAmount);

    // ROI 는 비율이라 분모가 작으면 값이 튄다. 재고금액 큰 순으로 쌓아 전체 자본의 80% 를 채우는
    // 상품까지만 후보로 둔다 — «돈이 많이 들어간 것 중 못 버는 것» 이 이 정렬의 물음이다
    let pool = items;
    if (sort === 'roi' && items.length) {
        const byStock = items.slice().sort((a, b) => b.stockAmount - a.stockAmount);
        const totalStock = byStock.reduce((s, p) => s + p.stockAmount, 0);
        if (totalStock > 0) {
            const cut = [];
            let acc = 0;
            for (const p of byStock) {
                cut.push(p);
                acc += p.stockAmount;
                if (acc >= totalStock * 0.8) break;
            }
            pool = cut;
        }
    }

    const picked = pool.slice().sort(cmp).slice(0, 10);
    // 막대는 보이는 10개 안에서 정규화한다. 전체 최댓값 기준이면 1등이 나머지를 눌러 실오라기가 된다
    const maxSales = Math.max(1, ...picked.map(p => p.saleAmount));
    const maxStock = Math.max(1, ...picked.map(p => p.stockAmount));
    const top = picked.map(p => Object.assign({}, p, {
        salesPct: Math.max(2, p.saleAmount / maxSales * 100),
        stockPct: Math.max(2, p.stockAmount / maxStock * 100)
    }));

    // 추린 10개가 전체에서 얼마나 큰 몫인지 — 안 보이면 10개가 전부인 줄 안다
    const lockedAll = items.reduce((s, p) => s + p.stockAmount, 0);
    const lockedTop = top.reduce((s, p) => s + p.stockAmount, 0);
    return {
        top,
        summary: {
            total: items.length,
            lockedTop,
            lockedRatio: lockedAll > 0 ? Math.round(lockedTop / lockedAll * 100) : 0,
            // 조용히 거르면 이게 전부인 줄 안다 — 화면에 밝힌다
            note: sort === 'roi' ? '자본 80%를 차지하는 상품 안에서' : ''
        }
    };
}
